import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import { Box, Button, DialogActions, DialogContent, Grid } from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import GenericNumberTextField from "../../components/GenericNumberTextField";
import GenericDatePicker from "../../components/GenericDatePicker";
import GenericAutoComplete from "../../components/GenericAutoComplete";
import MensagemErro from "../../components/MensagemErro";

const CondicaoComercialFormSchema = z
  .object({
    idCondicaoComercial: z.number().readonly(),
    idServico: z
      .number({
        required_error: "Serviço é obrigatório!",
        invalid_type_error: "Serviço é obrigatório!",
      })
      .min(1, {
        message: "Por favor, preencha o serviço!",
      }),
    validoDe: z.date({
      required_error: "Data inicial é obrigatório!",
    }),
    validoAte: z.date(),
    percentual: z.string(),
    imposto: z.string(),
    prazo: z.string(),
  })
  .partial({
    idCondicaoComercial: true,
    validoAte: true,
    percentual: true,
    imposto: true,
    prazo: true,
  })
  .required({
    idServico: true,
    validoDe: true,
  });

type CondicaoComercialFormData = z.infer<typeof CondicaoComercialFormSchema>;

type CondicaoComercialFormProps = {
  condicaoComercialValues: CondicaoComercialFormData;
  onSubmit(data: CondicaoComercialFormData): void;
  onClose(event: any): void;
};

export default function CondicaoComercialForm({
  condicaoComercialValues,
  onSubmit,
  onClose,
}: CondicaoComercialFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CondicaoComercialFormData>({
    mode: "onChange",
    resolver: zodResolver(CondicaoComercialFormSchema),
    defaultValues: {
      idCondicaoComercial:
        condicaoComercialValues.idCondicaoComercial || undefined,
      idServico: condicaoComercialValues.idServico || undefined,
      validoDe: new Date(condicaoComercialValues.validoDe + "T00:00:00") || undefined,
      validoAte: (condicaoComercialValues.validoAte) ? new Date(condicaoComercialValues.validoAte + "T00:00:00") : undefined,
      percentual: condicaoComercialValues.percentual || "0",
      imposto: condicaoComercialValues.imposto || "0",
      prazo: condicaoComercialValues.prazo || "0",
    },
  });

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  const [msg, setMsg] = useState("");
  const [openSnackbarErro, setOpenSnackbarErro] = useState(false);
  const handleOpenSnackbarErro = () => {
    setOpenSnackbarErro(true);
  };

  const submit = (data: CondicaoComercialFormData) => {
    const body: any = {
      condicaoComercial: {
        validoDe: data.validoDe,
        validoAte: data.validoAte,
        percentual: data.percentual,
        imposto: data.imposto,
        prazo: data.prazo,
        idServico: data.idServico,
      },
    };

    onSubmit(body);
  };

  const handleCloseSnackbarErro = (event: any, reason: any) => {
    if (reason !== "clickaway") {
      return;
    }
    setOpenSnackbarErro(false);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={1}>
              <Controller
                name="idCondicaoComercial"
                control={control}
                defaultValue={condicaoComercialValues.idCondicaoComercial}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    disabled
                    label="ID"
                    error={Boolean(errors.idCondicaoComercial)}
                    helperText={errors.idCondicaoComercial?.message}
                    placeholder={"ID"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={2}>
              <Controller
                name="validoDe"
                control={control}
                defaultValue={condicaoComercialValues.validoDe}
                render={({ field: { ref, ...field } }) => (
                  <GenericDatePicker
                    label="Válido De"
                    placeholder="Selecione a data inicial"
                    inputRef={ref}
                    error={Boolean(errors.validoDe)}
                    helperText={errors.validoDe?.message}
                    required
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={1.4}>
              <Controller
                name="percentual"
                control={control}
                defaultValue={condicaoComercialValues.percentual}
                render={({ field: { ref, ...field } }) => (
                  <GenericNumberTextField
                    label="Percentual"
                    error={Boolean(errors.percentual)}
                    helperText={errors.percentual?.message}
                    inputRef={ref}
                    className={styles.campoNumero}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={1.4}>
              <Controller
                name="imposto"
                control={control}
                defaultValue={condicaoComercialValues.imposto}
                render={({ field: { ref, ...field } }) => (
                  <GenericNumberTextField
                    label="Imposto"
                    error={Boolean(errors.imposto)}
                    helperText={errors.imposto?.message}
                    inputRef={ref}
                    className={styles.campoNumero}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={1.4}>
              <Controller
                name="prazo"
                control={control}
                defaultValue={condicaoComercialValues.prazo}
                render={({ field: { ref, ...field } }) => (
                  <GenericNumberTextField
                    label="Prazo"
                    error={Boolean(errors.prazo)}
                    helperText={errors.prazo?.message}
                    inputRef={ref}
                    className={styles.campoNumero}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4.8}>
              <Controller
                name="idServico"
                control={control}
                defaultValue={condicaoComercialValues.idServico}
                render={({ field: { ref, onChange, ...field } }) => (
                  <GenericAutoComplete
                    variant={'outlined'}
                    codigoCombo={"SERVICO"}
                    onChange={onChange}
                    selectionId={
                      condicaoComercialValues.idServico
                        ? condicaoComercialValues.idServico
                        : ""
                    }
                    error={errors.idServico}
                    helperText={errors.idServico?.message}
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{marginTop: '8px'}}>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
      <MensagemErro
        open={openSnackbarErro}
        handleClose={handleCloseSnackbarErro}
        msg={msg}
      />
    </form>
  );
}
